import {setPresence} from '../utils';

type ControlState = {
    disabled?: boolean;
    required?: boolean;
    invalid?: boolean;
};

type NativeControlState = ControlState & {
    name?: string;
    value?: string;
    readonly?: boolean;
    ariaRequired?: boolean;
    nativeRequired?: boolean;
};

type HiddenInputState = {
    name: string;
    values: string[];
    required?: boolean;
    form?: string;
    marker?: string;
};

type SyncControlPresenceOptions = {
    el: HTMLElement;
    state: ControlState;
    value?: string;
};

type SyncNativeControlOptions = {
    input: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
    state: NativeControlState;
};

type SyncRepeatedHiddenInputsOptions = {
    container: HTMLElement;
    state: HiddenInputState;
    currentKey?: string;
};

type DispatchNativeControlChangeOptions = {
    container: HTMLElement | null;
    fallback: HTMLElement;
    marker: string;
};

const syncControlPresence = ({
    el,
    state,
    value = '',
}: SyncControlPresenceOptions): void => {
    setPresence({el, attr: 'data-disabled', present: state.disabled ?? false, value});
    setPresence({el, attr: 'data-required', present: state.required ?? false, value});
    setPresence({el, attr: 'data-invalid', present: state.invalid ?? false, value});
};

const syncNativeControl = ({
    input,
    state,
}: SyncNativeControlOptions): void => {
    if (state.name !== undefined && state.name !== '') input.name = state.name;
    if (state.value !== undefined) input.value = state.value;

    input.disabled = state.disabled ?? false;
    input.required = state.nativeRequired ?? state.required ?? false;

    if ('readOnly' in input) {
        input.readOnly = state.readonly ?? false;
    }

    if (state.ariaRequired !== undefined) {
        input.setAttribute('aria-required', state.ariaRequired ? 'true' : 'false');
    }

    if (state.invalid) {
        input.setAttribute('aria-invalid', 'true');
    } else {
        input.removeAttribute('aria-invalid');
    }
};

const hiddenInputsKey = (state: HiddenInputState): string => JSON.stringify({
    name: state.name,
    values: state.values,
    required: state.required ?? false,
    form: state.form ?? '',
    marker: state.marker ?? 'data-ui-hidden-input',
});

const syncRepeatedHiddenInputs = ({
    container,
    state,
    currentKey = '',
}: SyncRepeatedHiddenInputsOptions): string => {
    const marker = state.marker ?? 'data-ui-hidden-input';
    const nextKey = hiddenInputsKey({...state, marker});
    if (nextKey === currentKey) return currentKey;

    container.replaceChildren();
    if (!state.name) return nextKey;

    state.values.forEach((value) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = state.name;
        input.value = value;
        input.setAttribute(marker, '');
        if (state.required) input.required = true;
        if (state.form) input.setAttribute('form', state.form);
        container.append(input);
    });

    return nextKey;
};

const dispatchNativeControlChange = ({
    container,
    fallback,
    marker,
}: DispatchNativeControlChangeOptions): void => {
    const control = container?.querySelector<HTMLInputElement>(`input[${marker}]`) ?? fallback;
    control.dispatchEvent(new Event('input', {bubbles: true}));
    control.dispatchEvent(new Event('change', {bubbles: true}));
};

export {
    dispatchNativeControlChange,
    syncControlPresence,
    syncNativeControl,
    syncRepeatedHiddenInputs,
};
export type {
    ControlState,
    DispatchNativeControlChangeOptions,
    HiddenInputState,
    NativeControlState,
    SyncControlPresenceOptions,
    SyncNativeControlOptions,
    SyncRepeatedHiddenInputsOptions,
};
